import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { isAbsoluteUrl } from '@utils/urlHelpers';
import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  InferGetServerSidePropsType,
} from 'next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { ssrClient } from '../../apollo/api/avantArteClient';
import MetaTags from '../../components/Content/MetaTags';
import Layout from '../../components/Layout';
import { LockScreen, ReleasePageContent } from '../../components/Releases';
import { ONE_HOUR_IN_SECONDS, RELEASE_PAGE_TYPE } from '../../constants';
import { AvantArteArtistsSearchGateway } from '../../gateways/AvantArteArtistsSearchGateway';
import { useReleasePageData } from '../../hooks/useReleasePageData';
import { ArtistSearchResult, AugmentedArtistCard } from '../../types/Artists';
import { ReleasePage } from '../../types/ReleaseContentType';
import { useTrackPageView } from '../../utils/analytics';
import { getRelatedArtists } from '../../utils/artists';
import { getStoryblokPaginated, useStoryblok } from '../../utils/storyblok';
import { pageTranslations } from '../../utils/translations';

type PageProps = {
  internalArtists: ArtistSearchResult[];
  relatedArtists: AugmentedArtistCard[];
  locale: string;
  locales: string[];
  preview: boolean;
  ogImageUrl: string;
  story: ReleasePage;
};

export async function getServerSideProps(
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<PageProps>> {
  const search = new AvantArteArtistsSearchGateway(ssrClient(context));
  const slug = context.params?.slug as string;

  const [translations, stories] = await Promise.all([
    pageTranslations(context.locale || 'en', [
      'releases',
      'artists',
      'framing',
    ]),
    getStoryblokPaginated<ReleasePage>({
      locale: context.locale,
      per_page: 1,
      preview: context.preview,
      resolve_relations: ['relatedArtist.artist'],
      starts_with: `releases/${slug}`,
    }),
  ]);

  const story = stories.find((s) => s.slug === slug);

  if (!story) {
    return {
      notFound: true,
    };
  }

  const internalArtists = await search.getArtistsBySlug(
    (story.content.relatedArtists || []).flatMap(({ artist }) =>
      artist && typeof artist !== 'string' ? [artist.slug] : [],
    ),
  );

  if (
    story.content.redirection &&
    story.content.redirection.cached_url !== ''
  ) {
    const { search: searchParams } = new URL(
      context.req.url ?? '',
      'https://avantarte.com',
    );
    const cachedUrl = story.content.redirection.cached_url;

    return {
      redirect: {
        destination: `${
          isAbsoluteUrl(cachedUrl || '') ? cachedUrl : `/${cachedUrl}`
        }${searchParams}`,
        permanent: true,
      },
    };
  }

  const relatedArtists = await getRelatedArtists();

  context.res.setHeader(
    'Cache-Control',
    `public, s-maxage=${ONE_HOUR_IN_SECONDS}`,
  );

  return {
    props: {
      internalArtists,
      locale: context.locale || 'en',
      locales: context.locales || [],
      preview: Boolean(context.preview),
      ogImageUrl: `${story.content.thumbnail.filename}/m/1200x630/filters:format(jpeg)`,
      story,
      relatedArtists,
      ...translations,
    },
  };
}

const Page = ({
  internalArtists,
  locale,
  locales,
  ogImageUrl,
  preview,
  relatedArtists,
  story,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const page = useStoryblok(story, preview, locale);
  const data = useReleasePageData(page);
  const router = useRouter();

  const unlockCode = /^\d{6}$/.test(page.content.unlockCode ?? '')
    ? page.content.unlockCode
    : undefined;

  const properties = useMemo(
    () => ({
      'artist name': data.artistName,
      context: 'release page',
      hasProducts: data.hasProducts,
      page: RELEASE_PAGE_TYPE,
      pageTitle: data.title,
    }),
    [data.artistName, data.hasProducts, data.title],
  );

  useTrackPageView(RELEASE_PAGE_TYPE, data.title, properties);

  const sourcesTriggeringRedirect = [
    'instagram_post_AA',
    'instagram_post_INS',
    'instagram_stories_AA',
    'instagram_stories_INS',
  ];
  const isForceRedirectEnabled =
    page.slug === 'mickalene-thomas-2025' &&
    sourcesTriggeringRedirect.includes(router.query.utm_source as string);
  const isForceRedirectOn = useFeatureIsOn(
    isForceRedirectEnabled ? 'release-page-force-redirect' : '',
  );
  if (isForceRedirectOn) {
    router.push({
      pathname: '/releases/mickalene-thomas-2025-test',
      query: router.query,
    });
  }

  return (
    <Layout
      description={page.content.seo?.description}
      locale={locale}
      locales={locales}
      ogImageUrl={ogImageUrl}
      title={page.content.seo?.title}
    >
      <MetaTags robots="noindex" />
      {unlockCode ? (
        <LockScreen
          title={page.content.title}
          thumbnail={page.content.thumbnail}
          unlockCode={unlockCode}
        >
          <ReleasePageContent
            analytics={properties}
            data={data}
            internalArtists={internalArtists}
            page={page}
            relatedArtists={relatedArtists}
          />
        </LockScreen>
      ) : (
        <ReleasePageContent
          analytics={properties}
          data={data}
          internalArtists={internalArtists}
          page={page}
          relatedArtists={relatedArtists}
        />
      )}
    </Layout>
  );
};

export default Page;
